import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import styles from './TabItem.module.scss';
import type { TabItemProps, TabItemTypeEnum } from './TabItem.types';
import { useTab } from '../Tabs.Context';
import useBuildVariantProps from '../../../../modules/theme/useBuildVariantProps';
import { ConditionalView } from '../../../utils/ConditionalView';
import { WrapTextNodes } from '../../../utils/WrapTextNodes';
import { Spinner } from '../../../../library';

const TabItem = (props: TabItemProps) => {
  const {
    value,
    defaultTab = false,
    onClick,
    children,
    className,
    leadingIcon,
    trailingIcon,
    loading,
    style: currentStyle,
    ...otherProps
  } = props;

  const { style: tabsStyle, currentTab, updateCurrentTab } = useTab();

  const style = currentStyle ?? tabsStyle;
  const type: TabItemTypeEnum = useMemo(() => (value === currentTab ? 'Selected' : 'Unselected'), [value, currentTab]);

  useEffect(() => {
    if (defaultTab && currentTab === '') {
      updateCurrentTab(value);
    }
  }, [defaultTab, value, updateCurrentTab, currentTab]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    updateCurrentTab(value);
    if (onClick) {
      onClick(e);
    }
  };

  let leadingIconComponent, trailingIconComponent, content, loadingComponent;

  // If the button is in loading state replace content with a loading indicator
  if (loading) {
    loadingComponent = (
      <Spinner className={styles.loadingIcon} />
    );
  }

  // Render leading icon if it is passed
  if (leadingIcon) {
    leadingIconComponent = <div className={styles.leadingIcon}>
      {leadingIcon}
    </div>;
  }

  // Render training icon if it is passed
  if (trailingIcon) {
    trailingIconComponent = <div className={styles.trailingIcon}>
      {trailingIcon}
    </div>;
  }

  if (children) {
    content = (
      <WrapTextNodes 
        noWrap
        className={styles.text}
      >
        {children}
      </WrapTextNodes>
    )
  }

  const variantProps = useBuildVariantProps(
    'tab-item',
    {
      style,
      type,
    }
  );

  return (
    <button
      className={cx(
        styles.tabItem,
        className,
      )}
      onClick={handleClick}
      {...otherProps}
      {...variantProps}
      role="tab"
      aria-selected={type === 'Selected'}
    >
      <div className={styles.contentContainer}>
        {leadingIconComponent}
        {content}
        {trailingIconComponent}
      </div>
      <ConditionalView condition={style === 'Underlined' && type === 'Selected'}>
        <div className={styles.underline} />
      </ConditionalView>
    </button>
  );
};
export default TabItem;
